import {
  ChangeDetectionStrategy,
  Component,
  inject,
  ViewEncapsulation,
} from '@angular/core';
import {
  WcgButtonComponent,
  WcgDialogComponent,
  WcgTextFieldAreaComponent,
} from '@wcg/frontend-framework';

import { DELETE_DIALOG_DATA, DeleteFormGroup } from '../../types';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { NgClass, NgTemplateOutlet } from '@angular/common';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    WcgDialogComponent,
    WcgTextFieldAreaComponent,
    ReactiveFormsModule,
    MatButtonModule,
    WcgButtonComponent,
    NgTemplateOutlet,
    NgClass,
  ],
  selector: 'wcg-delete-dialog',
  styleUrls: ['./delete-dialog.component.css'],
  template: `
    <wcg-dialog
      [title]="data.title"
      [description]="data.description"
      [size]="data.size!"
    >
      @let applyFlex = data.type === 'DELETE' && data.extraInfo;
      <div
        class="wcg-full"
        [ngClass]="{ 'delete-dialog-flex': applyFlex }"
        content
      >
        @switch (data.type) {
          @case ('DELETE') {
            @if (data.extraInfo) {
              <div class="!flex !flex-col !gap-3">
                @if (data.extraInfo.subTitle.length > 0) {
                  <h4>{{ data.extraInfo.subTitle }}</h4>
                }
                @if (data.extraInfo.description.length > 0) {
                  <p>{{ data.extraInfo.description }}</p>
                }
              </div>
            }
            <form [formGroup]="deleteFormGroup" novalidate>
              <wcg-text-field-area
                class="w-full"
                height="comfortable"
                [errorText]="data.errorText!"
                [formControl]="deleteFormGroup.controls[data.formControlName]!"
                [isRequired]="true"
                [label]="data.label"
                [width]="'x-large'"
              />
            </form>
          }
          @case ('MESSAGE') {
            <p class="delete-dialog__message" [innerHTML]="data.message"></p>
          }
          @case ('TEMPLATE') {
            @if (data.templateRef) {
              <ng-container [ngTemplateOutlet]="data.templateRef" />
            }
          }
        }
      </div>

      @if (data.buttons.cancelButtonText.length) {
        <div tertiary>
          <wcg-button
            [color]="data.buttons.cancelColor"
            data-testid="delete-dialog__cancel"
            (click)="cancel()"
          >
            {{ data.buttons.cancelButtonText }}
          </wcg-button>
        </div>
      }
      @if (data.buttons.deleteButtonText.length) {
        <div primary>
          <wcg-button
            [color]="data.buttons.primaryColor"
            (click)="confirm(data.type)"
            [isDisabled]="
              !deleteFormGroup.controls[data.formControlName]!.value.length &&
              data.type === 'DELETE'
            "
            data-testid="delete-dialog__confirm"
          >
            {{ data.buttons.deleteButtonText }}
          </wcg-button>
        </div>
      }
    </wcg-dialog>
  `,
})
export class DeleteDialogComponent {
  private readonly _dialogRef = inject(MatDialogRef<DeleteDialogComponent>);
  public readonly data = inject<DELETE_DIALOG_DATA>(MAT_DIALOG_DATA);
  private readonly _fb = inject(FormBuilder);

  deleteFormGroup = this._createForm();

  get formattedMessage(): string {
    return this.data.message.replace(/\n/g, '<br>');
  }

  confirm(type: 'DELETE' | 'MESSAGE' | 'TEMPLATE') {
    switch (type) {
      case 'MESSAGE':
        this._dialogRef.close(true);
        break;
      default:
        this._dialogRef.close(
          this.deleteFormGroup.get(this.data.formControlName)?.getRawValue(),
        );
        break;
    }
  }

  cancel() {
    this._dialogRef.close();
  }

  private _createForm(): FormGroup<DeleteFormGroup> {
    return this._fb.group({
      [`${this.data.formControlName}`]: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });
  }
}
